import { createElement, useEffect, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { notifications } from '@mantine/notifications';
import { subscribePosStream, type PosStreamEvent } from '../../../../services/pos-stream/pos-stream.service';
import { reportError } from '../../../../services/logs/logs.service';
import { employeeSessionFingerprint, logEmployeeSessionAudit } from '../../../../services/employee-session-audit/employee-session-audit.service';
import { employeesService } from '../../../../services/employees/employees.service';
import { orderlinesService } from '../../../../services/orderlines/orderlines.service';
import { type EmployeeView } from '../../../../services/employees/employees.service';
import { type ProductView } from '../../../../services/products/products.service';
import { type PaymentTerminalView } from '../../../../services/credit-payments/credit-payments.service';
import { type PrinterView } from '../../../../services/printers/printers.service';
import { type CategoryView } from '../../../../services/categories/categories.service';
import { type HappyHourView } from '../../../../services/happy-hour/happy-hour.service';
import { type SessionView } from '../../../../services/sessions/sessions.service';
import { type OrderView } from '../../../../services/orders/orders.service';
import { type OrderlineView } from '../../../../services/orderlines/orderlines.service';
import { type PaymentlineView } from '../../../../services/payments/payments.service';
import { type AreaTableView, type AreaView } from '../../../../services/areas/areas.service';
import { type VariantComboGroupView } from '../../../../services/variants-combos/variants-combos.service';
import { checkForStaleVersion } from '../../../../utils/app-version';
import { type RequestRefresh } from '../useDeferredRefresh';
import { INITIAL_TABLE_FORM, mergeById, normalizeArea, removeById } from '../../core/pos-shell-page.helpers';
import { type CanvasInteraction, type TableFormState } from '../../core/pos-shell-page.types';

// Block a resurrecting opened/upserted for a just-closed order id (SSE reordering race window).
const ORDER_RESURRECTION_GUARD_MS = 5000;

// Debounce after the last orderline.* of a discount/OTH burst before the missed-merge heal check.
const ORDERLINE_HEAL_SETTLE_MS = 600;
// Money tolerance when comparing the client-derived billable against the authoritative order total.
const ORDERLINE_HEAL_TOLERANCE = 0.005;
// Wake refresh policy: only force a deferred self refresh after at least 1 minute asleep.
const WAKE_REFRESH_MIN_SLEEP_MS = 60_000;

// Shared toast for the "no cash-drawer configured" case (auto paths via SSE + manual button).
export const CASH_DRAWER_NOT_CONFIGURED_TOAST = {
  color: 'orange',
  title: 'מגירת מזומן',
  message: 'לא הוגדרה מדפסת עם מגירת מזומן — המגירה לא נפתחה. הגדר מדפסת עם מגירה בהגדרות.',
  autoClose: 3000,
} as const;


type UsePosStreamSubscriptionParams = {
  tenantId: string;
  employeeSession: string | null;
  isManager: boolean;
  loadMergeLinks: () => Promise<void>;
  transferMutationInFlightRef: MutableRefObject<boolean>;
  activeOrdersRef: MutableRefObject<OrderView[]>;
  selectedOrderIdRef: MutableRefObject<number | null>;
  initialOrdersLoadedForSessionRef: MutableRefObject<number | null>;
  editingTableIdRef: MutableRefObject<number | null>;
  setActiveOrders: Dispatch<SetStateAction<OrderView[]>>;
  setOrderlines: Dispatch<SetStateAction<OrderlineView[]>>;
  setPaymentlines: Dispatch<SetStateAction<PaymentlineView[]>>;
  setPosProducts: Dispatch<SetStateAction<ProductView[]>>;
  setPosTerminals: Dispatch<SetStateAction<PaymentTerminalView[]>>;
  setActivePrinters: Dispatch<SetStateAction<PrinterView[]>>;
  setPosCategories: Dispatch<SetStateAction<CategoryView[]>>;
  setSettingsCategories: Dispatch<SetStateAction<CategoryView[]>>;
  applyVariantComboGroupUpserted: (productId: number, group: VariantComboGroupView) => void;
  applyVariantComboGroupRemoved: (productId: number, groupId: number) => void;
  setActiveSession: Dispatch<SetStateAction<SessionView | null>>;
  setAutoCloseTime: Dispatch<SetStateAction<string | null>>;
  setFloorBackground: Dispatch<SetStateAction<'white' | 'image' | 'primary'>>;
  setGeneralItemRequiresManagerApproval?: Dispatch<SetStateAction<boolean>>;
  setRefundCodeViaPush?: Dispatch<SetStateAction<boolean>>;
  onSessionDailyBriefUpdated: (
    event: Extract<PosStreamEvent, { type: 'session.daily_brief_updated' }>,
  ) => void;
  setActiveAreas: Dispatch<SetStateAction<AreaView[]>>;
  setSettingsAreas: Dispatch<SetStateAction<AreaView[]>>;
  setAvailableEmployees: Dispatch<SetStateAction<EmployeeView[]>>;
  setSettingsEmployees: Dispatch<SetStateAction<EmployeeView[]>>;
  setSettingsProducts: Dispatch<SetStateAction<ProductView[]>>;
  setHappyHour: Dispatch<SetStateAction<HappyHourView | null>>;
  setEditingTableId: Dispatch<SetStateAction<number | null>>;
  setTableForm: Dispatch<SetStateAction<TableFormState>>;
  onCreditTransactionResolved: (event: Extract<PosStreamEvent, { type: 'credit.transaction.resolved' }>) => void;
  onCreditTransactionPending: (event: Extract<PosStreamEvent, { type: 'credit.transaction.pending' }>) => void;
  onTapOnPhonePending: (event: Extract<PosStreamEvent, { type: 'credit.tap.pending' }>) => void;
  onTapOnPhoneReleased: (event: Extract<PosStreamEvent, { type: 'credit.tap.released' }>) => void;
  onApprovalEvent: (event: Extract<PosStreamEvent, { type: 'approval_request.created' | 'approval_request.decided' }>) => void;
  requestDeferredRefresh: RequestRefresh;
  onSessionAutoClosed: (sessionId: number) => void;
  interactionRef: MutableRefObject<CanvasInteraction | null>;
  currentEmployeeId: number | null;
  activeEmployeeSessionId: string | null;
  onEmployeeKicked: () => void;
  onOrderClosed?: (orderId: number) => void;
  onClosedOrderUpserted?: (order: OrderView) => void;
  shouldDeferOrderClosedCleanup?: (orderId: number) => boolean;
  // The locally-selected QC_ order just closed (e.g. bill-screen ביטול) → return to areas.
  onSelectedQuickChargeClosed?: () => void;
  // Connectivity signals feeding the offline banner: SSE connect failure / any live event.
  onStreamConnectionLost?: () => void;
  onStreamActivity?: () => void;
  // Missed-merge self-heal inputs: local lines + the same billable derivation used for יתרה.
  orderlinesRef?: MutableRefObject<OrderlineView[]>;
  getBillableTotalForOrder?: (order: OrderView | null | undefined, lines: OrderlineView[]) => number;
  isPaymentScreenOpen?: boolean;
};

export function usePosStreamSubscription({
  tenantId,
  employeeSession,
  isManager,
  loadMergeLinks,
  transferMutationInFlightRef,
  activeOrdersRef,
  selectedOrderIdRef,
  initialOrdersLoadedForSessionRef,
  editingTableIdRef,
  setActiveOrders,
  setOrderlines,
  setPaymentlines,
  setPosProducts,
  setPosTerminals,
  setActivePrinters,
  setPosCategories,
  setSettingsCategories,
  applyVariantComboGroupUpserted,
  applyVariantComboGroupRemoved,
  setActiveSession,
  setAutoCloseTime,
  setFloorBackground,
  setGeneralItemRequiresManagerApproval,
  setRefundCodeViaPush,
  onSessionDailyBriefUpdated,
  setActiveAreas,
  setSettingsAreas,
  setAvailableEmployees,
  setSettingsEmployees,
  setSettingsProducts,
  setHappyHour,
  setEditingTableId,
  setTableForm,
  onCreditTransactionResolved,
  onCreditTransactionPending,
  onTapOnPhonePending,
  onTapOnPhoneReleased,
  onApprovalEvent,
  requestDeferredRefresh,
  onSessionAutoClosed,
  interactionRef,
  currentEmployeeId,
  activeEmployeeSessionId,
  onEmployeeKicked,
  onOrderClosed,
  onClosedOrderUpserted,
  shouldDeferOrderClosedCleanup,
  onSelectedQuickChargeClosed,
  onStreamConnectionLost,
  onStreamActivity,
  orderlinesRef,
  getBillableTotalForOrder,
  isPaymentScreenOpen = false,
}: UsePosStreamSubscriptionParams): void {
  // Keep the paid-list refetch callback fresh without re-subscribing the EventSource on toggle.
  const onOrderClosedRef = useRef(onOrderClosed);
  onOrderClosedRef.current = onOrderClosed;
  const onClosedOrderUpsertedRef = useRef(onClosedOrderUpserted);
  onClosedOrderUpsertedRef.current = onClosedOrderUpserted;
  const shouldDeferOrderClosedCleanupRef = useRef(shouldDeferOrderClosedCleanup);
  shouldDeferOrderClosedCleanupRef.current = shouldDeferOrderClosedCleanup;
  const onSelectedQuickChargeClosedRef = useRef(onSelectedQuickChargeClosed);
  onSelectedQuickChargeClosedRef.current = onSelectedQuickChargeClosed;
  const onStreamConnectionLostRef = useRef(onStreamConnectionLost);
  onStreamConnectionLostRef.current = onStreamConnectionLost;
  const onStreamActivityRef = useRef(onStreamActivity);
  onStreamActivityRef.current = onStreamActivity;
  const isPaymentScreenOpenRef = useRef(isPaymentScreenOpen);
  isPaymentScreenOpenRef.current = isPaymentScreenOpen;
  const fullRefreshRequestedRef = useRef(false);
  // orderId -> close timestamp; a late opened/upserted for a recently-closed order is dropped.
  const recentlyClosedOrderIdsRef = useRef<Map<number, number>>(new Map());
  // Missed-merge heal state (kept fresh without re-subscribing the EventSource on prop identity).
  const getBillableTotalForOrderRef = useRef(getBillableTotalForOrder);
  getBillableTotalForOrderRef.current = getBillableTotalForOrder;
  const healInFlightRef = useRef(false);
  const healSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The order+authoritative-total awaiting a post-burst comparison; null when nothing is armed.
  const armedHealRef = useRef<{ orderId: number; total: number } | null>(null);
  // orderId -> last total we already refetched for, so the same total never heals twice (no loop).
  const healedTotalByOrderRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    fullRefreshRequestedRef.current = false;
  }, [employeeSession, tenantId]);

  useEffect(() => {
    if (!employeeSession || !tenantId) {
      return;
    }

    if (typeof window === 'undefined' || typeof window.EventSource !== 'function') {
      return;
    }

    // True while an order id sits inside the resurrection guard window; lazily prunes stale ids.
    const isOrderRecentlyClosed = (orderId: number): boolean => {
      const closedAt = recentlyClosedOrderIdsRef.current.get(orderId);
      if (closedAt === undefined) {
        return false;
      }
      if (Date.now() - closedAt > ORDER_RESURRECTION_GUARD_MS) {
        recentlyClosedOrderIdsRef.current.delete(orderId);
        return false;
      }
      return true;
    };

    // On a client-vs-authoritative total mismatch (a lost-frame burst) run ONE guarded refetch.
    const runHealCheck = () => {
      const armed = armedHealRef.current;
      armedHealRef.current = null;
      const deriveBillable = getBillableTotalForOrderRef.current;
      if (!armed || !orderlinesRef || !deriveBillable) {
        return;
      }
      const { orderId, total } = armed;
      if (orderId !== selectedOrderIdRef.current) {
        return;
      }
      const order = activeOrdersRef.current.find((entry) => entry.id === orderId) ?? null;
      const clientTotal = deriveBillable(order, orderlinesRef.current);
      if (Math.abs(clientTotal - total) <= ORDERLINE_HEAL_TOLERANCE) {
        return;
      }
      if (healInFlightRef.current || healedTotalByOrderRef.current.get(orderId) === total) {
        return;
      }
      healInFlightRef.current = true;
      healedTotalByOrderRef.current.set(orderId, total);
      void orderlinesService
        .listForOrder(tenantId, orderId, employeeSession)
        .then((lines) => {
          if (orderId === selectedOrderIdRef.current) {
            setOrderlines(lines);
          }
        })
        .catch(() => undefined)
        .finally(() => {
          healInFlightRef.current = false;
        });
    };

    const scheduleHealCheck = () => {
      if (healSettleTimerRef.current) {
        clearTimeout(healSettleTimerRef.current);
      }
      healSettleTimerRef.current = setTimeout(runHealCheck, ORDERLINE_HEAL_SETTLE_MS);
    };

    // Arm a post-burst comparison for the selected order from order.upserted's total.
    const armSelectedOrderHeal = (order: OrderView) => {
      if (!orderlinesRef || !getBillableTotalForOrderRef.current) {
        return;
      }
      if (order.id !== selectedOrderIdRef.current || order.totalAmount === undefined) {
        return;
      }
      armedHealRef.current = { orderId: order.id, total: order.totalAmount };
      scheduleHealCheck();
    };

    // A trailing orderline.* for the armed order pushes the settle window (debounce, last frame).
    const noteOrderlineActivityForHeal = (orderId: number) => {
      if (armedHealRef.current?.orderId === orderId) {
        scheduleHealCheck();
      }
    };

    const maybeNotifyCrossDeviceItemsSent = (event: {
      orderId: number;
      actorEmployeeId?: number;
      actorEmployeeName?: string;
      sentAmount?: number;
      skippedLineCount?: number;
    }) => {
      if (!isPaymentScreenOpenRef.current) {
        return;
      }
      if (event.orderId !== selectedOrderIdRef.current) {
        return;
      }
      const hasSentAmount = typeof event.sentAmount === 'number' && event.sentAmount > 0;
      const hasSkipped = typeof event.skippedLineCount === 'number' && event.skippedLineCount > 0;
      if (!hasSentAmount && !hasSkipped) {
        return;
      }
      if (typeof event.actorEmployeeId === 'number' && event.actorEmployeeId === currentEmployeeId) {
        return;
      }

      const actorName = event.actorEmployeeName?.trim() || 'עובד אחר';
      notifications.show({
        color: 'blue',
        title: `ההזמנה עודכנה על ידי ${actorName}`,
        message: createElement('span', { 'data-testid': 'cross-device-items-toast' }, 'נוספו פריטים חדשים להזמנה'),
        autoClose: 3500,
      });
    };

    const handleEvent = (event: PosStreamEvent) => {
      // Any live frame proves the stream is up; clears the offline banner's SSE signal.
      onStreamActivityRef.current?.();
      switch (event.type) {
        case 'order.opened': {
          const incomingOrder = event.order as OrderView;
          // Drop an opened that lost the race to its own close (same id already closed).
          if (isOrderRecentlyClosed(incomingOrder.id)) {
            return;
          }
          setActiveOrders((previous) => mergeById(previous, incomingOrder));
          return;
        }
        case 'order.upserted': {
          const upsertedOrder = event.order as OrderView;
          // Post-close upserts (e.g. guest-name edit from the receipt modal) patch the paid list.
          if (upsertedOrder.status === 'closed') {
            onClosedOrderUpsertedRef.current?.(upsertedOrder);
          }
          setActiveOrders((previous) => {
            // A closed order (e.g. post-close bill-printed upsert) must not be resurrected.
            if (upsertedOrder.status === 'closed') {
              return removeById(previous, upsertedOrder.id);
            }
            const existing = previous.find((entry) => entry.id === upsertedOrder.id);
            if (!existing) {
              // Only add a brand-new row if it was not just closed (late upsert after close).
              return isOrderRecentlyClosed(upsertedOrder.id)
                ? previous
                : mergeById(previous, upsertedOrder);
            }
            return mergeById(previous, { ...existing, ...upsertedOrder });
          });
          // Arm the missed-merge heal off the authoritative total before the orderline.* burst.
          armSelectedOrderHeal(upsertedOrder);
          return;
        }
        case 'order.closed': {
          const now = Date.now();
          recentlyClosedOrderIdsRef.current.forEach((closedAt, id) => {
            if (now - closedAt > ORDER_RESURRECTION_GUARD_MS) {
              recentlyClosedOrderIdsRef.current.delete(id);
            }
          });
          recentlyClosedOrderIdsRef.current.set(event.orderId, now);
          const deferSelectedCleanup = event.orderId === selectedOrderIdRef.current
            && shouldDeferOrderClosedCleanupRef.current?.(event.orderId) === true;
          if (!deferSelectedCleanup) {
            setActiveOrders((previous) => removeById(previous, event.orderId));
          }
          if (event.orderId === selectedOrderIdRef.current && !deferSelectedCleanup) {
            setOrderlines([]);
            setPaymentlines([]);
            onSelectedQuickChargeClosedRef.current?.();
          }
          // Paid-list delta: hand the closed id to the paid hook, which fetches just that one order
          // through the paid predicate and merges it (Rule 27 delta, no widened event).
          onOrderClosedRef.current?.(event.orderId);
          return;
        }
        case 'orderline.added': {
          noteOrderlineActivityForHeal(event.orderId);
          const incomingLine = event.orderline as OrderlineView;
          // Pending lines are NOT billable, so the list total (billable − discount + tips) does not
          // move when a line is added — only when it is fired. Bump lastActivityAt only.
          setActiveOrders((previous) =>
            previous.map((entry) =>
              entry.id === event.orderId
                ? {
                    ...entry,
                    lastActivityAt: incomingLine?.updatedAt ?? entry.lastActivityAt,
                  }
                : entry,
            ),
          );
          if (event.orderId === selectedOrderIdRef.current) {
            setOrderlines((previous) =>
              previous.some((line) => line.id === incomingLine.id)
                ? previous
                : [...previous, incomingLine],
            );
          }
          return;
        }
        case 'orderline.updated': {
          noteOrderlineActivityForHeal(event.orderId);
          const incomingLine = event.orderline as OrderlineView;
          // Editing a pending line's qty/price does not affect the billable list total (pending is
          // excluded); the total only changes once the line is fired. Bump lastActivityAt only.
          setActiveOrders((previous) =>
            previous.map((entry) =>
              entry.id === event.orderId
                ? {
                    ...entry,
                    lastActivityAt: incomingLine?.updatedAt ?? entry.lastActivityAt,
                  }
                : entry,
            ),
          );
          if (event.orderId === selectedOrderIdRef.current) {
            setOrderlines((previous) =>
              previous.some((line) => line.id === incomingLine.id)
                ? previous.map((line) =>
                    line.id === incomingLine.id
                      ? {
                          ...line,
                          ...incomingLine,
                          itemPaidQuantity: incomingLine.itemPaidQuantity ?? line.itemPaidQuantity ?? 0,
                        }
                      : line,
                  )
                : [...previous, { ...incomingLine, itemPaidQuantity: incomingLine.itemPaidQuantity ?? 0 }],
            );
          }
          return;
        }
        case 'orderline.item_payment_updated': {
          if (event.orderId === selectedOrderIdRef.current) {
            const paidQuantityById = new Map(
              event.items.map((item) => [item.orderlineId, item.paidQuantity] as const),
            );
            setOrderlines((previous) =>
              previous.map((line) =>
                paidQuantityById.has(line.id)
                  ? { ...line, itemPaidQuantity: paidQuantityById.get(line.id) ?? 0 }
                  : line,
              ),
            );
          }
          return;
        }
        case 'orderline.removed': {
          noteOrderlineActivityForHeal(event.orderId);
          // Soft-removing a pending line (qty→0) does not change the billable list total, so leave
          // the order entry untouched and only drop the line from the open cart.
          if (event.orderId === selectedOrderIdRef.current) {
            setOrderlines((previous) => previous.filter((line) => line.id !== event.orderlineId));
          }
          return;
        }
        case 'orderline.sent': {
          maybeNotifyCrossDeviceItemsSent(event);
          if (event.sentLineIds.length > 0) {
            setActiveOrders((previous) =>
              previous.map((entry) =>
                entry.id === event.orderId
                  ? { ...entry, hasSentLines: true }
                  : entry,
              ),
            );
          }
          if (event.orderId === selectedOrderIdRef.current) {
            const sentSet = new Set(event.sentLineIds);
            const skippedSet = new Set(event.skippedLineIds);
            setOrderlines((previous) =>
              previous.map((line) => {
                if (sentSet.has(line.id)) {
                  return { ...line, status: 'sent' };
                }
                if (skippedSet.has(line.id)) {
                  return { ...line, status: 'skipped' };
                }
                return line;
              }),
            );
          }
          return;
        }
        case 'paymentline.created': {
          if (event.orderId === selectedOrderIdRef.current) {
            setPaymentlines((previous) => mergeById(previous, event.paymentline as PaymentlineView));
          }
          return;
        }
        case 'paymentline.done': {
          if (event.orderId === selectedOrderIdRef.current) {
            setPaymentlines((previous) =>
              previous.map((line) =>
                line.id === event.paymentlineId ? { ...line, status: 'done' as const } : line,
              ),
            );
          }
          return;
        }
        case 'session.opened': {
          const opened = event.session as SessionView;
          setActiveSession(opened);
          setActiveOrders([]);
          initialOrdersLoadedForSessionRef.current = opened.id;
          return;
        }
        case 'session.closed': {
          setActiveSession((previous) =>
            previous && previous.id === event.sessionId ? null : previous,
          );
          setActiveOrders([]);
          if (event.autoClosed) {
            notifications.show({
              color: 'orange',
              title: 'סגירת קופה אוטומטית',
              message: 'הקופה נסגרת אוטומטית',
              autoClose: 3000,
            });
            onSessionAutoClosed(event.sessionId);
          }
          return;
        }
        case 'cashDrawer.notConfigured': {
          notifications.show(CASH_DRAWER_NOT_CONFIGURED_TOAST);
          return;
        }

        case 'session.cash_alterations_updated': {
          setActiveSession((previous) =>
            previous && previous.id === event.sessionId
              ? { ...previous, cashAlterations: event.cashAlterations }
              : previous,
          );
          return;
        }
        case 'session.daily_brief_updated': {
          setActiveSession((previous) =>
            previous && previous.id === event.sessionId
              ? { ...previous, dailyBrief: event.dailyBrief }
              : previous,
          );
          onSessionDailyBriefUpdated(event);
          return;
        }
        case 'session.auto_close_time_updated': {
          setAutoCloseTime(event.autoCloseTime);
          return;
        }
        case 'tenant.floor_background_updated': {
          setFloorBackground(event.floorBackground);
          return;
        }
        case 'tenant.general_item_requires_manager_approval_updated': {
          setGeneralItemRequiresManagerApproval?.(event.generalItemRequiresManagerApproval);
          return;
        }
        case 'tenant.refund_code_via_push_updated': {
          setRefundCodeViaPush?.(event.refundCodeViaPush);
          return;
        }
        case 'area.upserted': {
          const incoming = normalizeArea(event.area as AreaView);
          const incomingForActiveAreas: AreaView = {
            ...incoming,
            tables: incoming.tables.filter((table) => table.isActive),
          };
          setActiveAreas((previous) => {
            if (!incomingForActiveAreas.isActive) {
              return removeById(previous, incomingForActiveAreas.id);
            }
            return mergeById(previous, incomingForActiveAreas);
          });
          if (isManager) {
            setSettingsAreas((previous) => mergeById(previous, incoming));
          }
          return;
        }
        case 'table.upserted': {
          const incomingTable = event.table as AreaTableView;
          const targetAreaId = event.areaId;
          if (interactionRef.current?.tableId === incomingTable.id) {
            return;
          }
          const upsertTableInArea = (area: AreaView): AreaView => {
            if (area.id !== targetAreaId) {
              return area;
            }
            return { ...area, tables: mergeById(area.tables, incomingTable) };
          };
          const removeTableFromArea = (area: AreaView): AreaView => {
            if (area.id !== targetAreaId) {
              return area;
            }
            return { ...area, tables: removeById(area.tables, incomingTable.id) };
          };
          const mergeTableIntoArea = (area: AreaView): AreaView => {
            if (area.id !== targetAreaId) {
              return area;
            }
            return { ...area, tables: mergeById(area.tables, incomingTable) };
          };
          setActiveAreas((previous) => previous.map(incomingTable.isActive ? upsertTableInArea : removeTableFromArea));
          if (isManager) {
            setSettingsAreas((previous) => previous.map(mergeTableIntoArea));
          }
          return;
        }
        case 'table.removed': {
          const removedTableId = event.tableId;
          const targetAreaId = event.areaId;
          const removeTableFromArea = (area: AreaView): AreaView => {
            if (area.id !== targetAreaId) {
              return area;
            }
            return { ...area, tables: removeById(area.tables, removedTableId) };
          };
          setActiveAreas((previous) => previous.map(removeTableFromArea));
          if (isManager) {
            setSettingsAreas((previous) => previous.map(removeTableFromArea));
          }
          if (editingTableIdRef.current === removedTableId) {
            setEditingTableId(null);
            setTableForm(INITIAL_TABLE_FORM);
          }
          return;
        }
        case 'employee.upserted': {
          const incoming = event.employee as EmployeeView;
          setAvailableEmployees((previous) =>
            incoming.isActive ? mergeById(previous, incoming) : removeById(previous, incoming.id),
          );
          if (isManager) {
            setSettingsEmployees((previous) => mergeById(previous, incoming));
          }
          return;
        }
        case 'employee.deactivated': {
          setAvailableEmployees((previous) => removeById(previous, event.employeeId));
          if (isManager) {
            setSettingsEmployees((previous) =>
              previous.map((employee) =>
                employee.id === event.employeeId ? { ...employee, isActive: false } : employee,
              ),
            );
          }
          return;
        }
        case 'employee.kicked': {
          if (event.employeeId === currentEmployeeId && event.kickedSessionId === activeEmployeeSessionId) {
            logEmployeeSessionAudit('sse_kick_matched_picker_reset', {
              tenantId,
              employeeId: event.employeeId,
              kickedSessionId: employeeSessionFingerprint(event.kickedSessionId),
            });
            reportError({
              level: 'warn',
              errorName: 'EmployeeSessionKicked',
              message: 'Employee session cleared after a matching takeover event',
              context: { reason: 'employee-kicked-sse' },
            });
            notifications.show({
              color: 'yellow',
              title: 'התנתקות ממכשיר',
              message: 'המשתמש נותק ממכשיר זה בעקבות התחברות ממכשיר אחר',
              autoClose: 3000,
            });
            onEmployeeKicked();
          } else {
            logEmployeeSessionAudit('sse_kick_ignored_not_current_session', {
              tenantId,
              eventEmployeeId: event.employeeId,
              currentEmployeeId,
              kickedSessionId: employeeSessionFingerprint(event.kickedSessionId),
              activeSessionId: employeeSessionFingerprint(activeEmployeeSessionId),
            });
          }
          return;
        }
        case 'employee.presence.probe.requested': {
          if (employeeSession && activeEmployeeSessionId && event.targetSessionId === activeEmployeeSessionId) {
            void employeesService.acknowledgePresenceProbe(tenantId, employeeSession, event.probeId);
          }
          return;
        }
        case 'product.upserted': {
          const incoming = event.product as ProductView;
          setPosProducts((previous) =>
            incoming.isActive ? mergeById(previous, incoming) : removeById(previous, incoming.id),
          );
          if (isManager) {
            setSettingsProducts((previous) => mergeById(previous, incoming));
          }
          return;
        }
        case 'product.deactivated': {
          setPosProducts((previous) => removeById(previous, event.productId));
          if (isManager) {
            setSettingsProducts((previous) =>
              previous.map((product) =>
                product.id === event.productId ? { ...product, isActive: false } : product,
              ),
            );
          }
          return;
        }
        case 'payment-terminal.upserted': {
          const incoming = event.terminal as PaymentTerminalView;
          // Mirror CreditPaymentsService.listActiveTerminals' contract exactly: active terminal,
          // active pinpads only, drop the terminal entirely if that leaves zero pinpads — so a
          // pinpad-level toggle (not just the terminal-level one) reaches the POS picker live.
          const activePinpads = incoming.pinpads.filter((pinpad) => pinpad.isActive);
          const shouldShow = incoming.isActive && activePinpads.length > 0;
          setPosTerminals((previous) =>
            shouldShow
              ? mergeById(previous, { ...incoming, pinpads: activePinpads })
              : removeById(previous, incoming.id),
          );
          return;
        }
        case 'payment-terminal.active-changed': {
          setPosTerminals((previous) =>
            event.isActive
              ? previous
              : removeById(previous, event.terminalId),
          );
          return;
        }
        case 'category.upserted': {
          const incoming = event.category as CategoryView;
          setPosCategories((previous) =>
            incoming.isActive ? mergeById(previous, incoming) : removeById(previous, incoming.id),
          );
          if (isManager) {
            setSettingsCategories((previous) => mergeById(previous, incoming));
          }
          return;
        }
        case 'printer.upserted': {
          const incoming = event.printer as PrinterView;
          // Drop a disabled/inactive printer from the POS routing snapshot; merge otherwise.
          const routable = incoming.active && incoming.humanDisabled !== true;
          setActivePrinters((previous) =>
            routable ? mergeById(previous, incoming) : removeById(previous, incoming.id),
          );
          return;
        }
        case 'happy_hour.changed': {
          const incoming = event.happyHour as HappyHourView;
          setHappyHour(incoming);
          notifications.show({
            color: incoming.active ? 'green' : 'gray',
            title: 'Happy Hour',
            message: incoming.active
              ? `Happy Hour הופעל — ${incoming.discountPct ?? 0}% הנחה`
              : 'Happy Hour כובה',
            autoClose: 3000,
          });
          return;
        }
        case 'variantComboGroup.upserted': {
          applyVariantComboGroupUpserted(event.productId, event.group);
          return;
        }
        case 'variantComboGroup.removed': {
          applyVariantComboGroupRemoved(event.productId, event.groupId);
          return;
        }
        case 'merge.links.changed': {
          if (!transferMutationInFlightRef.current) {
            void loadMergeLinks();
          }
          return;
        }
        case 'credit.transaction.pending': {
          // Cross-device in-flight lock: freeze this paymentline (and its אפס reset) on every device until the resolved event lands.
          onCreditTransactionPending(event);
          return;
        }
        case 'credit.tap.pending': {
          onTapOnPhonePending(event);
          return;
        }
        case 'credit.tap.released': {
          onTapOnPhoneReleased(event);
          return;
        }
        case 'credit.transaction.resolved': {
          // Authoritative credit outcome (approved/declined/unknown) so a dropped charge HTTP response is recovered here.
          onCreditTransactionResolved(event);
          return;
        }
        case 'approval_request.created':
        case 'approval_request.decided': {
          onApprovalEvent(event);
          return;
        }
        case 'pos.refresh-requested': {
          // Manager + session open/close broadcasts all route into the same defer gate.
          requestDeferredRefresh(event.reason ?? 'manager');
          return;
        }
        default:
          return;
      }
    };

    const requestFullRefresh = () => {
      if (fullRefreshRequestedRef.current) {
        return;
      }
      fullRefreshRequestedRef.current = true;
      requestDeferredRefresh('self');
    };

    const handleEventFailure = () => {
      setActiveOrders([]);
      setOrderlines([]);
      setPaymentlines([]);
      setActiveAreas([]);
      setSettingsAreas([]);
      setPosProducts([]);
      setPosCategories([]);
      setSettingsCategories([]);
      setAvailableEmployees([]);
      setSettingsEmployees([]);
      setSettingsProducts([]);
      requestFullRefresh();
    };

    const unsubscribe = subscribePosStream(
      tenantId,
      employeeSession,
      handleEvent,
      handleEventFailure,
      () => onStreamConnectionLostRef.current?.(),
      requestFullRefresh,
      // After a long sleep, reload only for a fresh bundle (Last-Event-ID replays missed SSE).
      (inactiveMs) => {
        onStreamActivityRef.current?.();
        if ((inactiveMs ?? 0) >= WAKE_REFRESH_MIN_SLEEP_MS) {
          void checkForStaleVersion().then((version) => {
            if (version) {
              requestDeferredRefresh('version', version);
            }
          });
        }
      },
      // Every (re)open is a liveness signal so the offline banner clears even on an idle stream.
      () => onStreamActivityRef.current?.(),
    );

    return () => {
      if (healSettleTimerRef.current) {
        clearTimeout(healSettleTimerRef.current);
        healSettleTimerRef.current = null;
      }
      armedHealRef.current = null;
      unsubscribe();
    };
  }, [
    employeeSession,
    tenantId,
    isManager,
    loadMergeLinks,
    transferMutationInFlightRef,
    activeOrdersRef,
    orderlinesRef,
    initialOrdersLoadedForSessionRef,
    selectedOrderIdRef,
    editingTableIdRef,
    setActiveOrders,
    setOrderlines,
    setPaymentlines,
    setPosProducts,
    setActivePrinters,
    setPosCategories,
    setSettingsCategories,
    applyVariantComboGroupUpserted,
    applyVariantComboGroupRemoved,
    setActiveSession,
    setAutoCloseTime,
    setFloorBackground,
    onSessionDailyBriefUpdated,
    setActiveAreas,
    setSettingsAreas,
    setAvailableEmployees,
    setSettingsEmployees,
    setSettingsProducts,
    setHappyHour,
    setEditingTableId,
    setTableForm,
    onCreditTransactionResolved,
    onCreditTransactionPending,
    onTapOnPhonePending,
    onTapOnPhoneReleased,
    onApprovalEvent,
    requestDeferredRefresh,
    onSessionAutoClosed,
    interactionRef,
    currentEmployeeId,
    activeEmployeeSessionId,
    onEmployeeKicked,
  ]);
}
